/**
 * Age band labels, sourced from the `size_age_bands` metaobject via GM_STATE.
 *
 * Variants carry a bare `utopia.age_group` value ("Baby", "Older") because that is what the
 * warehouse and Utopia use. Both size treatments resolve their band wording through here, so the
 * stacked captions and the consolidated pills can never disagree.
 *
 * Delivered on GM_STATE rather than as a PDP attribute because the bands are product-independent,
 * which also gets the wishlist drawer the same labels without any Liquid in its path.
 */

/**
 * Display order for the stacked rows and the pill tabs. Bands not listed here keep their arrival
 * order and follow the known ones, so a new Utopia value is appended rather than dropped.
 */
export const BAND_ORDER = ['Baby', 'Toddler', 'Younger', 'Older']

export const getAgeBands = () => {
  const bands = window.GM_STATE?.metaobjects?.size_age_bands
  return Array.isArray(bands) ? bands : []
}

/**
 * The customer-facing wording for one `age_group` value, falling back to the raw value so an
 * unmapped band — or an empty metaobject — still renders a usable row.
 *
 * @param {string} ageGroup Raw `utopia.age_group` value.
 * @param {{short?: boolean, bands?: object[]}} options `short` picks the pill wording.
 * @returns {string}
 */
export const bandLabel = (ageGroup, { short = false, bands = getAgeBands() } = {}) => {
  const match = bands.find((band) => band?.ageGroup === ageGroup)
  if (!match) return ageGroup
  if (short) return match.shortLabel || match.label || ageGroup
  return match.label || ageGroup
}

/**
 * The band's name on its own ("Younger Kids"), for the pill's first line above the short label.
 *
 * @param {string} ageGroup Raw `utopia.age_group` value.
 * @returns {string}
 */
export const bandDisplayName = (ageGroup, bands = getAgeBands()) =>
  bands.find((band) => band?.ageGroup === ageGroup)?.displayName || ageGroup

/**
 * BAND_ORDER first, then anything unrecognised in the order it arrived.
 *
 * @param {string[]} ageGroups Distinct `age_group` values off the variants.
 * @returns {string[]}
 */
export const sortBands = (ageGroups = []) => {
  const known = BAND_ORDER.filter((band) => ageGroups.includes(band))
  const unknown = ageGroups.filter((band) => !BAND_ORDER.includes(band))
  return [...known, ...unknown]
}
